import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import Button from "../button/Button";
import ComponentState, { ComponentStateTypes } from "../ComponentState";
import PageHeader from "./PageHeader";
import useAppContext from "utilities/hooks/useAppContext";
import lodash from "lodash";
import ButtonDropdown from "../button-dropdown/ButtonDropdown";
import { faListDots } from "@fortawesome/free-solid-svg-icons";

export type PageActionsType =
  | React.ReactNode
  | (() => React.ReactNode)
  | {
      icon: IconDefinition;
      link: string | (() => void);
      hide?: boolean | (() => boolean) | string;
      title: string;
      name: string;
      className?: string;
    }[];

export type PagePropsType = {
  title: string | React.ReactNode;
  actions?: PageActionsType;
  children: React.ReactNode | React.ReactNode[];
  wFull?: boolean;
  state?: ComponentStateTypes;
  removeHeader?: boolean;
};

const Page = (props: PagePropsType) => {
  const context = useAppContext();

  const moreButton = (
    <Button theme="link" title={context.t("button.more")}>
      <FontAwesomeIcon icon={faListDots} />
    </Button>
  );

  const isHiddenItem = (hide: boolean | (() => boolean) | string | undefined) => {
    if (typeof hide == "boolean") return hide;
    else if (typeof hide == "function") return hide();
    else if (typeof hide == "string") return !context.userHasRole(hide);
    return false;
  };

  const renderActions = () => {
    const SHOW_MORE_BUTTON_LIMIT = 3;
    if (Array.isArray(props.actions)) {
      const list = props.actions.filter((i) => !isHiddenItem(i.hide));
      if (list.length == 0) return <></>;
      const showLength = list.length <= SHOW_MORE_BUTTON_LIMIT + 1 ? list.length : SHOW_MORE_BUTTON_LIMIT;
      const toShow = lodash.take(list, showLength).map((a) => {
        if (typeof a.link == "function") {
          return (
            <Button key={`page-action-${a.name}`} theme="link" onClick={a.link} title={a.title || "button"}>
              <FontAwesomeIcon icon={a.icon} />
            </Button>
          );
        } else {
          return (
            <Link key={`page-action-${a.name}`} className={`btn link ${a.className || ""}`.trim()} to={a.link} title={a.title}>
              <FontAwesomeIcon icon={a.icon} />
            </Link>
          );
        }
      });

      const toHide = lodash.drop(list, showLength).map((a) => {
        if (typeof a.link == "function") {
          return (
            <Button key={`page-action-${a.name}`} theme="link" onClick={a.link} title={a.title || "button"}>
              <span>
                <FontAwesomeIcon icon={a.icon} /> {context.t(a.title)}
              </span>
            </Button>
          );
        } else {
          return (
            <Link key={`page-action-${a.name}`} className={`btn link ${a.className || ""}`.trim()} to={a.link} title={a.title}>
              <FontAwesomeIcon icon={a.icon} /> {context.t(a.title)}
            </Link>
          );
        }
      });

      if (toHide.length) {
        toShow.push(
          <ButtonDropdown key="more-action-button-page-actions" button={moreButton} className="my-auto">
            <ul className="flex flex-col">
              {toHide.map((x) => (
                <li key={x.key + "-button"}>{x}</li>
              ))}
            </ul>
          </ButtonDropdown>,
        );
      }

      return <>{toShow}</>;
    } else if (typeof props.actions === "object") return <>{props.actions}</>;
    else if (typeof props.actions === "function") return <>{props.actions()}</>;
    return <></>;
  };

  return (
    <div className="lg:px-5">
      {!props.removeHeader && <PageHeader title={props.title}>{renderActions()}</PageHeader>}
      <div className={`${props.wFull ? "w-full" : "sm:w-max md:max-w-vw-70 lg:max-w-vw-50 2xl:max-w-vw-30"} px-2`}>
        <ComponentState state={props.state}>{props.children}</ComponentState>
      </div>
    </div>
  );
};

export default Page;
